import React from 'react';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { alpha, useTheme, type Theme } from '@mui/material/styles';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import type { ChatFolderColorTier } from 'utils/chat-view-context-title';

/** גוונים עדינים — אדום / ירוק / כחול / סגול */
function resolveFolderAccentColor(tier: ChatFolderColorTier, theme: Theme): string {
  const isDark = theme.palette.mode === 'dark';

  switch (tier) {
    case 'unassigned':
      return isDark ? theme.palette.error.light : theme.palette.error.dark;
    case 'all':
      return isDark ? theme.palette.success.light : theme.palette.success.dark;
    case 'mine':
      return isDark ? theme.palette.primary.light : theme.palette.primary.dark;
    case 'other':
      return isDark ? '#D1A8E0' : '#7B5194';
  }
}

export type ChatViewContextTitleRowProps = {
  folderLabel: string;
  viewLabel: string;
  folderTier: ChatFolderColorTier;
  /** טולטיפ — כותרת צפופה יותר */
  dense?: boolean;
};

/** שורת «תיקייה ◂ תצוגה» — זהה לכותרת מעל רשימת השיחות */
const ChatViewContextTitleRow: React.FC<ChatViewContextTitleRowProps> = ({
  folderLabel,
  viewLabel,
  folderTier,
  dense = false,
}) => {
  const theme = useTheme();
  const folderColor = resolveFolderAccentColor(folderTier, theme);
  const isDark = theme.palette.mode === 'dark';

  return (
    <Stack
      direction="row"
      alignItems="center"
      flexWrap="nowrap"
      useFlexGap
      sx={{
        width: '100%',
        minWidth: 0,
        columnGap: 0.5,
        rowGap: 0.25,
        textAlign: 'start',
      }}
    >
      <Box
        component="span"
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          maxWidth: dense ? 120 : 160,
          px: 0.75,
          py: 0.125,
          borderRadius: 1,
          flexShrink: 0,
          bgcolor: alpha(folderColor, isDark ? 0.14 : 0.07),
          border: '1px solid',
          borderColor: alpha(folderColor, isDark ? 0.26 : 0.16),
        }}
      >
        <Typography
          component="span"
          variant="caption"
          noWrap
          title={folderLabel}
          sx={{ color: folderColor, fontWeight: 600, lineHeight: 1.35, opacity: 0.92 }}
        >
          {folderLabel}
        </Typography>
      </Box>

      <ChevronLeftIcon
        sx={{ fontSize: dense ? 14 : 15, color: 'text.disabled', flexShrink: 0, opacity: 0.7 }}
        aria-hidden
      />

      <Typography
        component="span"
        variant={dense ? 'caption' : 'subtitle2'}
        color="text.primary"
        noWrap
        title={viewLabel}
        sx={{
          fontWeight: 600,
          lineHeight: 1.35,
          minWidth: 0,
          flex: '1 1 auto',
        }}
      >
        {viewLabel}
      </Typography>
    </Stack>
  );
};

export default ChatViewContextTitleRow;
